(() => {
  const VERSION = '2.2.0';
  if (window.PBUSBCatalogV2?.version === VERSION) return;

  const V = window.PBVariantV2;
  const catalog = window.PBUSBProductCatalogV2 || {};
  if (!V || !Object.keys(catalog).length) {
    window.PBUSBCatalogV2 = { version: VERSION, active: false };
    return;
  }

  let config = {};
  try { config = JSON.parse(document.querySelector('[data-pb-logo-v3-config]')?.textContent || '{}'); } catch {}

  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => root ? [...root.querySelectorAll(selector)] : [];

  const priceCents = variant => {
    const raw = variant?.price ?? 0;
    const value = Number(raw || 0);
    if (!Number.isFinite(value)) return 0;
    return String(raw).includes('.') ? Math.round(value * 100) : Math.round(value);
  };

  const money = cents => new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD'
  }).format(Number(cents || 0) / 100);

  const capacity = variant => {
    const values = [
      ...(variant?.options || []),
      variant?.option1 || '', variant?.option2 || '', variant?.option3 || ''
    ];
    for (const value of values) {
      const match = String(value || '').trim().match(/^(\d+)\s*(?:GB|G)$/i);
      if (match) return `${Number(match[1])}GB`;
    }
    return '';
  };

  const variantState = variant => V.stateForVariant(variant, 'usb');
  const isAvailable = variant => variant?.available !== false;

  const stateVariants = (product, state = 'none') => (product?.variants || []).filter(variant =>
    variantState(variant) === state && capacity(variant)
  );

  const sellableVariants = (product, state = 'none') => stateVariants(product, state).filter(isAvailable);

  const capacitiesFor = product => [...new Set(stateVariants(product, 'none').map(capacity))]
    .sort((a, b) => Number(a.replace(/\D/g, '')) - Number(b.replace(/\D/g, '')));

  // Match capacities canonically so legacy values such as 128G and 128GB are equivalent,
  // without ever inventing a capacity that is absent from this product's own variants.
  const variantFor = (product, cap, state = 'none') =>
    stateVariants(product, state).find(variant => capacity(variant) === cap) || null;

  const lowestPrice = product => {
    const prices = sellableVariants(product, 'none').map(priceCents).filter(value => value > 0);
    return prices.length ? Math.min(...prices) : null;
  };

  const setLabel = (button, text) => {
    if (!button) return;
    let label = q(button, '.pb-usb-addon__price-label');
    if (!label) {
      label = document.createElement('strong');
      label.className = 'pb-usb-addon__price-label';
      button.appendChild(label);
    }
    label.textContent = text || '';
    label.hidden = !text;
  };

  const ensureThumb = (button, product) => {
    if (!button || !product?.thumbnail_image) return;
    let thumb = q(button, '.pb-usb-addon__thumb');
    if (!thumb) {
      thumb = document.createElement('span');
      thumb.className = 'pb-usb-addon__thumb';
      button.prepend(thumb);
    }
    let image = q(thumb, 'img');
    if (!image) {
      image = document.createElement('img');
      image.loading = 'lazy';
      thumb.appendChild(image);
    }
    image.src = product.thumbnail_image;
    image.alt = product.title || '';
  };

  const fittedExclusions = new Set(['maple_wood_crystal', 'walnut_wood_crystal']);
  const isExcluded = key => config.productHandle === 'linen-fitted-usb-box-for-crystal-usb-drives' && fittedExclusions.has(key);

  const hideCarrierGrid = carrierGrid => {
    carrierGrid.dataset.pbCapacityCarrier = 'true';
    carrierGrid.hidden = true;
    carrierGrid.setAttribute('aria-hidden', 'true');
    carrierGrid.style.setProperty('display', 'none', 'important');
  };

  const initRoot = root => {
    const modal = q(root, '.pb-photo-customizer__modal, .pb-usb-customizer__modal, [data-pb-customizer-modal]') || root;
    const styleButtons = qa(modal, '[data-usb-key]');
    const carrierGrid = q(modal, '[data-pb-usb-capacities]');
    const carriers = qa(carrierGrid, '[data-usb-capacity]');
    const addUsbButton = q(modal, '[data-usb-choice="add"]');
    if (!styleButtons.length || !carrierGrid || !carriers.length) return null;

    hideCarrierGrid(carrierGrid);

    let dynamicGrid = q(modal, '[data-pb-usb-capacities-v2]');
    if (!dynamicGrid) {
      dynamicGrid = document.createElement('div');
      dynamicGrid.className = 'pb-usb-addon__pill-grid';
      dynamicGrid.dataset.pbUsbCapacitiesV2 = '';
      carrierGrid.after(dynamicGrid);
    }

    // The modal is portaled to document.body when opened. Keep explicit references/state
    // instead of querying the original product root after that move.
    let selectedKeyValue = styleButtons.find(button => button.classList.contains('is-active'))?.dataset.usbKey
      || styleButtons[0]?.dataset.usbKey || '';

    const selectedKey = () => selectedKeyValue;
    const activeCarrier = () => q(carrierGrid, '[data-usb-capacity].is-active');
    const selectedCapacity = () => activeCarrier()?.dataset.usbCapacity || '';

    const activateCarrier = cap => {
      if (!cap) return;
      const carrier = activeCarrier() || carriers[0];
      if (carrier.dataset.usbCapacity === cap && carrier.classList.contains('is-active')) return;
      carrier.dataset.usbCapacity = cap;
      const span = q(carrier, 'span');
      if (span) span.textContent = cap;
      else {
        const textNode = [...carrier.childNodes].find(node => node.nodeType === Node.TEXT_NODE);
        if (textNode) textNode.textContent = cap;
        else carrier.prepend(document.createTextNode(cap));
      }
      carrier.click();
    };

    const renderStyles = () => {
      let globalLowest = null;
      styleButtons.forEach(button => {
        const key = button.dataset.usbKey;
        const product = catalog[key];
        const excluded = isExcluded(key);
        const price = product && !excluded ? lowestPrice(product) : null;
        button.hidden = excluded;
        button.disabled = !product || excluded || price === null;
        if (product && !excluded) ensureThumb(button, product);
        setLabel(button, price !== null ? `from +${money(price)}` : excluded ? 'Does not fit this box' : 'Unavailable');
        if (price !== null) globalLowest = globalLowest === null ? price : Math.min(globalLowest, price);
      });
      setLabel(addUsbButton, globalLowest !== null ? `from +${money(globalLowest)}` : 'Unavailable');
    };

    const renderCapacities = ({ force = false } = {}) => {
      hideCarrierGrid(carrierGrid);
      const key = selectedKey();
      const product = catalog[key];
      const caps = isExcluded(key) ? [] : capacitiesFor(product);
      const current = selectedCapacity();
      const sellableCaps = caps.filter(cap => isAvailable(variantFor(product, cap, 'none')));
      const chosen = sellableCaps.includes(current)
        ? current
        : sellableCaps[0] || caps[0] || '';

      if (chosen && (force || current !== chosen)) activateCarrier(chosen);

      dynamicGrid.innerHTML = '';
      caps.forEach(cap => {
        const variant = variantFor(product, cap, 'none');
        const availableNow = Boolean(variant && isAvailable(variant));
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pb-usb-addon__pill';
        button.dataset.pbUsbCapacityV2 = cap;
        button.disabled = !availableNow;
        button.setAttribute('aria-disabled', availableNow ? 'false' : 'true');
        if (cap === (selectedCapacity() || chosen)) button.classList.add('is-active');

        const label = document.createElement('span');
        label.textContent = cap;
        const price = document.createElement('strong');
        price.className = 'pb-usb-addon__price-label';
        price.textContent = availableNow ? money(priceCents(variant)) : 'Unavailable';
        button.append(label, price);

        if (availableNow) {
          button.addEventListener('click', event => {
            event.preventDefault();
            activateCarrier(cap);
            qa(dynamicGrid, '[data-pb-usb-capacity-v2]').forEach(el => el.classList.toggle('is-active', el === button));
            queueMicrotask(() => renderCapacities());
          });
        }
        dynamicGrid.appendChild(button);
      });

      if (!caps.length) {
        const unavailable = document.createElement('p');
        unavailable.className = 'pb-logo-v3__muted';
        unavailable.textContent = 'No capacities are configured for this USB style.';
        dynamicGrid.appendChild(unavailable);
      }
    };

    styleButtons.forEach(button => button.addEventListener('click', () => {
      selectedKeyValue = button.dataset.usbKey || selectedKeyValue;
      queueMicrotask(() => { renderStyles(); renderCapacities({ force: true }); });
      setTimeout(() => { renderStyles(); renderCapacities(); }, 80);
    }));

    renderStyles();
    renderCapacities({ force: true });

    return { root, modal, renderStyles, renderCapacities, selectedKey: () => selectedKeyValue };
  };

  const roots = qa(document, '[id^="PBUSBBoxCustomizer-"]').map(initRoot).filter(Boolean);

  window.PBUSBCatalogV2 = {
    version: VERSION,
    active: true,
    catalog,
    roots,
    refresh: () => roots.forEach(ctx => { ctx.renderStyles(); ctx.renderCapacities(); })
  };
})();

/* Shared customizer visual repair. This asset is routed on every customizer page. */
(() => {
  const VERSION = '2.2.0';
  if (window.PBCustomizerVisualRepairV2?.version === VERSION) return;

  const ROOT_SELECTOR = '[id^="PBPhotoBoxCustomizer-"], [id^="PBUSBBoxCustomizer-"], [id^="PBStandaloneUSBCustomizer-"]';
  const MODAL_SELECTOR = '.pb-photo-customizer__modal, .pb-usb-customizer__modal, .pb-standalone-usb-customizer__modal, [data-pb-customizer-modal]';
  const contexts = [];

  const installPortalQueryBridge = (root, scope) => {
    if (!root || !scope || root.__pbCustomizerPortalQueryBridge) return;
    const directQuerySelector = root.querySelector.bind(root);
    const directQuerySelectorAll = root.querySelectorAll.bind(root);
    const portalQuerySelector = scope.querySelector.bind(scope);
    const portalQuerySelectorAll = scope.querySelectorAll.bind(scope);

    root.querySelector = selector => {
      const direct = directQuerySelector(selector);
      if (direct || root.contains(scope)) return direct;
      if (scope.matches?.(selector)) return scope;
      return portalQuerySelector(selector);
    };

    root.querySelectorAll = selector => {
      const direct = directQuerySelectorAll(selector);
      if (direct.length || root.contains(scope)) return direct;
      return portalQuerySelectorAll(selector);
    };

    root.__pbCustomizerPortalQueryBridge = { version: '1.0.0', scope };
  };

  const q = (root, selector) => root?.querySelector(selector) || null;
  const qa = (root, selector) => root ? [...root.querySelectorAll(selector)] : [];
  const setHidden = (el, value) => { if (el && el.hidden !== value) el.hidden = value; };
  const active = (root, selector) => q(root, `${selector}.is-active`);
  const activeData = (root, selector, key, fallback = '') => active(root, selector)?.dataset?.[key] || fallback;
  const parse = (root, selector, fallback) => {
    try { return JSON.parse(q(root, selector)?.textContent || JSON.stringify(fallback)); }
    catch { return fallback; }
  };
  const normalizeKey = value => String(value || '')
    .trim().toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

  const ensureStyles = () => {
    if (document.getElementById('pb-customizer-visual-repair-v2-style')) return;
    const style = document.createElement('style');
    style.id = 'pb-customizer-visual-repair-v2-style';
    style.textContent = `
      [data-pb-usb-capacities][data-pb-capacity-carrier="true"]{display:none!important;visibility:hidden!important;height:0!important;min-height:0!important;margin:0!important;padding:0!important;overflow:hidden!important}
      .pb-logo-v3__preview--usb{
        left:10.35%!important;
        top:38.55%!important;
        width:40.3%!important;
        height:16.9%!important;
      }
    `;
    document.head.appendChild(style);
  };

  const familyFor = root => {
    if (root.id.startsWith('PBStandaloneUSBCustomizer-')) return 'standalone';
    if (root.id.startsWith('PBUSBBoxCustomizer-')) return 'usb-box';
    return 'photo';
  };

  const boxLogoActive = ctx => !!active(ctx.scope, '[data-pb-logo="box"]');
  const boxTextActive = ctx => !!active(ctx.scope, '[data-personalization="personalized"]') && !boxLogoActive(ctx);
  const usbLogoActive = ctx => !!active(ctx.scope, '[data-pb-logo="usb"]');
  const usbTextActive = ctx => !!active(ctx.scope, '[data-usb-engraving="yes"]') && !usbLogoActive(ctx);
  const addonLogoActive = ctx => !!active(ctx.scope, '[data-pb-logo="addon"]');
  const addonTextActive = ctx => !!active(ctx.scope, '[data-box-personalization="personalized"]') && !addonLogoActive(ctx);

  const selectedUsbProduct = ctx => {
    if (ctx.family !== 'usb-box') return null;
    const products = parse(ctx.root, '[data-pb-usb-products-json]', {});
    const key = activeData(ctx.scope, '[data-usb-key]', 'usbKey');
    return products?.[key] || null;
  };

  const usbIsWood = ctx => {
    if (ctx.family === 'standalone') return String(ctx.root.dataset.usbType || '').toLowerCase().includes('wood');
    const product = selectedUsbProduct(ctx);
    return String(product?.type || '').toLowerCase().includes('wood');
  };

  const setImage = (ctx, src) => {
    if (!src) return;
    const image = q(ctx.scope, '[data-pb-preview-image]');
    if (image && image.src !== src) image.src = src;
  };

  const syncBackground = ctx => {
    const owner = ctx.owner;

    if (ctx.family === 'photo') {
      const previews = parse(ctx.root, '[data-pb-preview-json]', {});
      const color = activeData(ctx.scope, '[data-color]', 'color', 'Cream');
      setImage(ctx, previews?.[color]);
      return;
    }

    if (ctx.family === 'usb-box') {
      if (owner === 'box') {
        const previews = parse(ctx.root, '[data-pb-preview-json]', {});
        const color = activeData(ctx.scope, '[data-color]', 'color', 'Cream');
        setImage(ctx, previews?.[color]);
        return;
      }

      if (owner === 'usb') {
        const product = selectedUsbProduct(ctx);
        const personalized = usbTextActive(ctx) || usbLogoActive(ctx);
        const src = personalized
          ? product?.engraving_preview_image || product?.featured_image
          : product?.featured_image;
        setImage(ctx, src);
      }
      return;
    }

    if (ctx.family === 'standalone') {
      if (owner === 'usb') {
        const previews = parse(ctx.root, '[data-pb-usb-preview-json]', {});
        const personalized = usbTextActive(ctx) || usbLogoActive(ctx);
        setImage(ctx, personalized ? previews?.engraving_preview_image || previews?.featured_image : previews?.featured_image);
        return;
      }

      if (owner === 'addon') {
        const products = parse(ctx.root, '[data-pb-box-products-json]', {});
        const key = activeData(ctx.scope, '[data-box-key]', 'boxKey');
        const color = activeData(ctx.scope, '[data-box-color]', 'boxColor');
        const product = products?.[key];
        const colorImage = product?.preview_images?.[normalizeKey(color)];
        setImage(ctx, colorImage || product?.featured_image);
      }
    }
  };

  const clearTextClasses = text => {
    if (!text) return;
    text.classList.remove(
      'pb-photo-customizer__preview-text--usb',
      'pb-photo-customizer__preview-text--box',
      'pb-usb-font-goudy',
      'pb-usb-font-montserrat',
      'pb-usb-font-alegreya',
      'pb-usb-font-roboto-slab',
      'pb-usb-font-oswald',
      'pb-usb-font-great-vibes'
    );
  };

  const appendLine = (text, value, className) => {
    if (!value) return;
    const line = document.createElement('div');
    line.className = className;
    line.textContent = value;
    text.appendChild(line);
  };

  const renderBoxText = (ctx, addon = false) => {
    const text = q(ctx.scope, '[data-pb-preview-text]');
    if (!text) return;
    const main = q(ctx.scope, addon ? '[data-pb-box-main-text]' : '[data-pb-main-input]')?.value?.trim() || '';
    const second = q(ctx.scope, addon ? '[data-pb-box-second-text]' : '[data-pb-second-input]')?.value?.trim() || '';
    const foil = active(ctx.scope, addon ? '[data-box-foil-name]' : '[data-foil]');
    const foilColor = addon ? foil?.dataset?.boxFoilColor : foil?.dataset?.foilColor;

    const currentMain = q(text, '.pb-photo-customizer__preview-line--main')?.textContent || '';
    const currentSecond = q(text, '.pb-photo-customizer__preview-line--second')?.textContent || '';
    const wrongMode = text.classList.contains('pb-photo-customizer__preview-text--usb');
    if (wrongMode || currentMain !== main || currentSecond !== second) {
      clearTextClasses(text);
      if (addon) text.classList.add('pb-photo-customizer__preview-text--box');
      text.innerHTML = '';
      appendLine(text, main, 'pb-photo-customizer__preview-line pb-photo-customizer__preview-line--main');
      appendLine(text, second, 'pb-photo-customizer__preview-line pb-photo-customizer__preview-line--second');
    }
    text.style.removeProperty('--pb-usb-engraving-size');
    text.style.color = foilColor || '#C8A95A';
    setHidden(text, false);
    text.style.removeProperty('display');
    text.removeAttribute('aria-hidden');
  };

  const renderUsbTextFallback = ctx => {
    const text = q(ctx.scope, '[data-pb-preview-text]');
    const input = q(ctx.scope, '[data-pb-usb-engraving-text]');
    if (!text || !input) return;

    const lines = String(input.value || '').split('\n').map(v => v.trim()).filter(Boolean).slice(0, 3);
    const currentLines = qa(text, '.pb-photo-customizer__preview-line--usb').map(line => line.textContent || '');
    const font = String(q(ctx.scope, '[data-pb-usb-engraving-font]')?.value || 'goudy').toLowerCase().replace(/\s+/g, '-');
    const expectedFontClass = `pb-usb-font-${font}`;
    const needsRender = currentLines.length !== lines.length || currentLines.some((value, index) => value !== lines[index]);
    if (needsRender) {
      clearTextClasses(text);
      text.classList.add('pb-photo-customizer__preview-text--usb', expectedFontClass);
      text.innerHTML = '';
      lines.forEach(value => appendLine(text, value, 'pb-photo-customizer__preview-line pb-photo-customizer__preview-line--usb'));
    } else {
      text.classList.add('pb-photo-customizer__preview-text--usb');
      ['pb-usb-font-goudy','pb-usb-font-montserrat','pb-usb-font-alegreya','pb-usb-font-roboto-slab','pb-usb-font-oswald','pb-usb-font-great-vibes'].forEach(cls => {
        if (cls !== expectedFontClass) text.classList.remove(cls);
      });
      text.classList.add(expectedFontClass);
    }

    text.style.setProperty('color', usbIsWood(ctx) ? '#1D0200' : '#ffffff', 'important');
    setHidden(text, false);
    text.style.removeProperty('display');
    text.removeAttribute('aria-hidden');
  };

  const hideText = ctx => {
    const text = q(ctx.scope, '[data-pb-preview-text]');
    if (!text) return;
    setHidden(text, true);
    text.style.setProperty('display', 'none', 'important');
    text.setAttribute('aria-hidden', 'true');
  };

  const syncText = ctx => {
    if (ctx.owner === 'box' && boxTextActive(ctx)) {
      renderBoxText(ctx, false);
      return;
    }
    if (ctx.owner === 'addon' && addonTextActive(ctx)) {
      renderBoxText(ctx, true);
      return;
    }
    if (ctx.owner === 'usb' && usbTextActive(ctx)) {
      renderUsbTextFallback(ctx);
      return;
    }
    hideText(ctx);
  };

  const syncLogos = ctx => {
    const boxPreview = q(ctx.scope, '[data-pb-logo-v3-preview="box"]');
    const usbPreview = q(ctx.scope, '[data-pb-logo-v3-preview="usb"]');
    const addonPreview = q(ctx.scope, '[data-pb-logo-v3-preview="addon"]');

    if (boxPreview) {
      const foil = active(ctx.scope, '[data-foil]');
      if (foil?.dataset?.foilColor) {
        boxPreview.style.color = foil.dataset.foilColor;
        q(boxPreview, '.pb-logo-v3__placeholder')?.style.setProperty('color', foil.dataset.foilColor, 'important');
      }
      setHidden(boxPreview, !(ctx.owner === 'box' && boxLogoActive(ctx)));
    }

    if (addonPreview) {
      const foil = active(ctx.scope, '[data-box-foil-name]');
      if (foil?.dataset?.boxFoilColor) {
        addonPreview.style.color = foil.dataset.boxFoilColor;
        q(addonPreview, '.pb-logo-v3__placeholder')?.style.setProperty('color', foil.dataset.boxFoilColor, 'important');
      }
      setHidden(addonPreview, !(ctx.owner === 'addon' && addonLogoActive(ctx)));
    }

    if (usbPreview) {
      const usbColor = usbIsWood(ctx) ? '#1D0200' : '#ffffff';
      usbPreview.style.color = usbColor;
      q(usbPreview, '.pb-logo-v3__placeholder')?.style.setProperty('color', usbColor, 'important');
      setHidden(usbPreview, !(ctx.owner === 'usb' && usbLogoActive(ctx)));
    }
  };

  const sanitizeCustomerCopy = root => {
    qa(root || document, '.pb-repair-config-note, .pb-repair-toast, [data-pb-message]').forEach(el => {
      const text = String(el.textContent || '');
      let next = text
        .replace(/ is not configured in Shopify yet\./g, ' is not configured yet.')
        .replace(/ exists in Shopify but is currently unavailable for sale\./g, ' is currently unavailable.')
        .replace(/ exists, but Shopify currently marks it as unavailable\. Check the variant inventory\/settings\./g, ' is currently unavailable.')
        .replace(/ exists, but Shopify currently marks it as unavailable\./g, ' is currently unavailable.');
      if (next !== text) el.textContent = next;
    });
  };

  const sync = ctx => {
    ctx.owner = ctx.scope.dataset.pbPreviewOwner || ctx.owner || (ctx.family === 'standalone' ? 'usb' : 'box');
    syncBackground(ctx);
    syncText(ctx);
    syncLogos(ctx);
    sanitizeCustomerCopy(document);
  };

  const schedule = ctx => {
    if (!ctx || ctx.syncQueued) return;
    ctx.syncQueued = true;
    const run = () => {
      ctx.syncQueued = false;
      sync(ctx);
    };
    // Microtask correction happens after legacy event handlers but before the next paint,
    // preventing the one-frame USB flash while editing box text/foil.
    queueMicrotask(run);
    requestAnimationFrame(() => sync(ctx));
    setTimeout(() => sync(ctx), 80);
    setTimeout(() => sync(ctx), 280);
  };

  const ownerFromTarget = target => {
    if (!(target instanceof Element)) return '';
    if (target.closest('[data-box-choice], [data-box-key], [data-box-color], [data-pb-box-personalization], [data-box-personalization], [data-box-foil-name], [data-pb-box-main-text], [data-pb-box-second-text], [data-pb-logo="addon"], [data-pb-logo-v3="addon"]')) return 'addon';
    if (target.closest('[data-usb-choice], [data-usb-key], [data-pb-usb-capacities], [data-pb-usb-capacities-v2], [data-pb-standalone-capacities-v2], [data-pb-usb-engraving], [data-usb-engraving], [data-pb-usb-engraving-text], [data-pb-usb-engraving-font], [data-pb-logo="usb"], [data-pb-logo-v3="usb"]')) return 'usb';
    if (target.closest('[data-color], [data-pb-personalization], [data-personalization], [data-foil], [data-pb-main-input], [data-pb-second-input], [data-pb-logo="box"], [data-pb-logo-v3="box"]')) return 'box';
    return '';
  };

  ensureStyles();

  qa(document, ROOT_SELECTOR).forEach(root => {
    const scope = q(root, MODAL_SELECTOR) || root;
    installPortalQueryBridge(root, scope);
    const ctx = {
      root,
      scope,
      family: familyFor(root),
      owner: scope.dataset.pbPreviewOwner || (familyFor(root) === 'standalone' ? 'usb' : 'box')
    };
    contexts.push(ctx);
    sync(ctx);

    new MutationObserver(() => schedule(ctx)).observe(scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'hidden']
    });
  });

  const handleEvent = event => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const ctx = contexts.find(item => item.scope.contains(target) || item.root.contains(target));
    if (!ctx) return;
    const owner = ownerFromTarget(target);
    if (owner) {
      ctx.owner = owner;
      ctx.scope.dataset.pbPreviewOwner = owner;
    }
    schedule(ctx);
  };

  document.addEventListener('click', handleEvent, true);
  document.addEventListener('input', handleEvent, true);
  document.addEventListener('change', handleEvent, true);

  sanitizeCustomerCopy(document);

  window.PBCustomizerVisualRepairV2 = {
    preview: (root, owner) => {
      const ctx = contexts.find(item => item.root === root);
      if (!ctx) return;
      ctx.owner = owner;
      ctx.scope.dataset.pbPreviewOwner = owner;
      sync(ctx);
    },
    version: VERSION,
    refresh: () => contexts.forEach(sync)
  };
})();

